import React from "react";
import { toast } from "react-toastify";
import GenericFormDialog, { Field } from "../GenericFormDialog";
import { countries } from "../../assets/utils/countries";
import {
  createCoach,
  updateCoach,
} from "../../services/CoachesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { Coach } from "./types";
import {
  ACTIVE_OPTIONS,
  COMPENSATION_OPTIONS,
  fromActiveLabel,
  isCoachActive,
  isCustomPerClassCompensation,
  toActiveLabel,
} from "./classUtils";

interface CoachFormDialogProps {
  open: boolean;
  coach: Coach | null;
  onClose: () => void;
  onSaved: () => void;
}

const countryOptions = countries.map((c) => ({
  label: `${c.label} (+${c.phone})`,
  value: c.code,
}));

/**
 * Create sets up the login, the COACH role and the wage config in one call, so
 * the create form asks for identity + wage; edit only touches the mutable bits
 * the API accepts (name, bio, wage, active).
 */
const CoachFormDialog: React.FC<CoachFormDialogProps> = ({
  open,
  coach,
  onClose,
  onSaved,
}) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [compensationType, setCompensationType] = React.useState("HOURLY");
  const isEdit = Boolean(coach);
  const isCustomPerClass = isCustomPerClassCompensation(compensationType);

  React.useEffect(() => {
    if (open) {
      setCompensationType(
        coach?.coach_profile?.compensation_type || "HOURLY"
      );
    }
  }, [open, coach]);

  const fields: Field[] = React.useMemo(() => {
    const wageFields: Field[] = [
      {
        name: "compensation_type",
        label: "Compensation Type",
        type: "dropdown",
        options: COMPENSATION_OPTIONS.map((o) => ({
          label: o.label,
          value: o.value,
        })),
        required: true,
      },
      // No fixed rate for CUSTOM_PER_CLASS — each session's amount is
      // entered by hand from the payout screen after it's done.
      ...(isCustomPerClass
        ? []
        : [
            {
              name: "compensation_rate",
              label:
                compensationType === "MONTHLY"
                  ? "Rate (per month)"
                  : compensationType === "HOURLY"
                  ? "Rate (per hour)"
                  : "Rate (per class)",
              type: "number" as const,
              required: true,
            },
          ]),
      {
        name: "active",
        label: "Status",
        type: "dropdown",
        options: ACTIVE_OPTIONS,
        required: true,
      },
      { name: "bio", label: "Bio", colSpan: 2 },
    ];

    if (isEdit) {
      return [
        { name: "full_name", label: "Full Name", required: true },
        ...wageFields,
      ];
    }

    return [
      { name: "full_name", label: "Full Name", required: true },
      {
        name: "country",
        label: "Country",
        type: "dropdown",
        options: countryOptions,
        searchable: true,
        required: true,
      },
      { name: "phone", label: "Phone", required: true },
      { name: "email", label: "Email" },
      ...wageFields,
    ];
  }, [isEdit, isCustomPerClass, compensationType]);

  // Memoized so the dialog's internal values are not reset on parent re-renders.
  const initialValues = React.useMemo(() => {
    if (coach) {
      return {
        full_name: String(coach.full_name || ""),
        bio: String(coach.bio ?? coach.coach_profile?.bio ?? ""),
        compensation_type: String(
          coach.coach_profile?.compensation_type || "HOURLY"
        ),
        compensation_rate: String(coach.coach_profile?.compensation_rate ?? ""),
        active: toActiveLabel(isCoachActive(coach)),
      };
    }
    return {
      full_name: "",
      country: "",
      phone: "",
      email: "",
      bio: "",
      compensation_type: "HOURLY",
      compensation_rate: "",
      active: "Active",
    };
  }, [coach]);

  const handleSubmit = async (values: Record<string, any>) => {
    const customPerClass = isCustomPerClassCompensation(values.compensation_type);

    let rate: number | undefined;
    if (!customPerClass) {
      rate = Number(values.compensation_rate);
      if (!Number.isFinite(rate) || rate < 0) {
        toast.error("Compensation rate must be a positive number.");
        return;
      }
    }

    setSubmitting(true);
    try {
      if (coach) {
        await updateCoach(String(coach.id), {
          full_name: String(values.full_name).trim(),
          bio: values.bio ? String(values.bio) : undefined,
          compensation_type: values.compensation_type,
          // Omitted entirely for CUSTOM_PER_CLASS — not sent as null/0.
          ...(customPerClass ? {} : { compensation_rate: rate }),
          active: fromActiveLabel(values.active),
        });
        toast.success("Coach updated successfully!");
      } else {
        await createCoach({
          full_name: String(values.full_name).trim(),
          country: String(values.country),
          phone: String(values.phone).trim(),
          email: values.email ? String(values.email).trim() : undefined,
          bio: values.bio ? String(values.bio) : undefined,
          compensation_type: values.compensation_type,
          ...(customPerClass ? {} : { compensation_rate: rate }),
          active: fromActiveLabel(values.active),
        });
        toast.success("Coach created successfully!");
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save coach."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GenericFormDialog
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Coach" : "Add Coach"}
      submitLabel={isEdit ? "Save" : "Create"}
      fields={fields}
      initialValues={initialValues}
      isSubmitting={submitting}
      onValuesChange={(values) => {
        const next = String(values.compensation_type || "HOURLY");
        setCompensationType((prev) => (prev === next ? prev : next));
      }}
      onSubmit={handleSubmit}
    />
  );
};

export default CoachFormDialog;
